import { faPrint } from '@fortawesome/pro-solid-svg-icons/faPrint';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons/faSpinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Stack, Text } from '@mantine/core';
import { useIntl } from 'react-intl';

import { getDocumentTypeLabel } from './document-type-label';
import { documentsSectionMessages } from './documents-section-messages';
import styles from './DocumentsSection.module.css';
import { DetailSection } from '../detail-section/DetailSection';

import type { LtlDocument } from '../../../../types/ltl';

type DocumentsSectionProps = {
  documents?: LtlDocument[];
  isDocumentsLoading?: boolean;
  onPrintDocument: (document: LtlDocument) => void;
};

/**
 * Right-column Documents card (§6.5). Renders one row per document returned by
 * the backend for the shipment, each with a tertiary Print button that invokes
 * `onPrintDocument(document)`. Shows a spinner while documents are generating,
 * and an empty-state message when there are no documents.
 * Purely callback-driven — no transaction id needed.
 */
export const DocumentsSection = ({ documents, isDocumentsLoading, onPrintDocument }: DocumentsSectionProps) => {
  const intl = useIntl();
  const hasDocuments = (documents?.length ?? 0) > 0;

  const DocumentLoader = (
    <Stack align='center' gap='xs'>
      <FontAwesomeIcon icon={faSpinner} spin />
      <Stack align='center' gap={4}>
        <Text fw={600} size='sm'>
          {intl.formatMessage(documentsSectionMessages.creatingDocuments)}
        </Text>
        <Text size='sm'>{intl.formatMessage(documentsSectionMessages.creatingDocumentsSubtext)}</Text>
      </Stack>
    </Stack>
  );

  const DocumentsComponent = (
    <div className={styles.rows}>
      {documents?.map((doc) => (
        <div className={styles.row} key={doc.key}>
          <span className={styles.label}>{intl.formatMessage(getDocumentTypeLabel(doc.documentType))}</span>
          <Button
            data-testid={`print-document-${doc.key}`}
            leftSection={<FontAwesomeIcon icon={faPrint} />}
            onClick={() => onPrintDocument(doc)}
            size='xs'
            variant='ghost'
          >
            {intl.formatMessage(documentsSectionMessages.print)}
          </Button>
        </div>
      ))}
    </div>
  );

  const EmptyDocuments = <span className={styles.label}>{intl.formatMessage(documentsSectionMessages.noDocuments)}</span>;

  return (
    <DetailSection testId='documents-section' title={intl.formatMessage(documentsSectionMessages.sectionTitle)}>
      {isDocumentsLoading && DocumentLoader}
      {!isDocumentsLoading && (hasDocuments ? DocumentsComponent : EmptyDocuments)}
    </DetailSection>
  );
};
